'use client';
import { jsxs, jsx } from "react/jsx-runtime";
import { cn, formatNumberWithZeroPrefix } from "@tameeni/shared-logic";
import React__default, { useCallback, useState, useEffect } from "react";
import { Skeleton } from "../skeleton/skeleton.js";
const separatorMap = {
  Comma: ",",
  Colon: ":"
};
const UnMemoizedTimer = ({
  expiresOn,
  deadline,
  title,
  onCountdownFinish,
  on5MinutesLeft,
  descriptionClassName,
  containerClassName,
  separator = "Colon",
  customSeparator,
  isTimeout,
  disableDay,
  disableHours = false,
  disableMinutes = false,
  disableSeconds = false,
  isLoading,
  locale = "en",
  shouldLocalizeNumbers,
  hasLabels,
  customTimeLabels = {}
}) => {
  const calculateMillisecondsRemainingFromUnits = (units) => {
    let totalMilliseconds = 0;
    if (units.days) totalMilliseconds += units.days * 24 * 60 * 60 * 1e3;
    if (units.hours) totalMilliseconds += units.hours * 60 * 60 * 1e3;
    if (units.minutes) totalMilliseconds += units.minutes * 60 * 1e3;
    if (units.seconds) totalMilliseconds += units.seconds * 1e3;
    return totalMilliseconds;
  };
  const calculateRemainingMilliseconds = useCallback(() => {
    if (expiresOn) {
      const now = (/* @__PURE__ */ new Date()).getTime();
      const expiryTime = new Date(expiresOn).getTime();
      return expiryTime - now;
    }
    return calculateMillisecondsRemainingFromUnits(deadline || {});
  }, [expiresOn, deadline]);
  const [remainingTime, setRemainingTime] = useState(
    calculateRemainingMilliseconds()
  );
  const [count, setCount] = useState(0);
  const calculateTimeRemaining = (milliseconds) => {
    const days2 = Math.floor(milliseconds / (1e3 * 60 * 60 * 24));
    const hours2 = Math.floor(milliseconds / (1e3 * 60 * 60) % 24);
    const minutes2 = Math.floor(milliseconds / 1e3 / 60 % 60);
    const seconds2 = Math.floor(milliseconds / 1e3 % 60);
    return { days: days2, hours: hours2, minutes: minutes2, seconds: seconds2 };
  };
  const { days, hours, minutes, seconds } = calculateTimeRemaining(remainingTime);
  const timeUnits = [
    ...!disableDay ? [
      {
        value: days,
        label: customTimeLabels.day || "days",
        labelAr: customTimeLabels.day || "أيام"
      }
    ] : [],
    ...!disableHours ? [
      {
        value: hours,
        label: customTimeLabels.hour || "hours",
        labelAr: customTimeLabels.hour || "ساعات"
      }
    ] : [],
    ...!disableMinutes ? [
      {
        value: minutes,
        label: customTimeLabels.minute || "mins",
        labelAr: customTimeLabels.minute || "دقائق"
      }
    ] : [],
    ...!disableSeconds ? [
      {
        value: seconds,
        label: customTimeLabels.second || "sec",
        labelAr: customTimeLabels.second || "ثواني"
      }
    ] : []
  ];
  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTime((prevRemainingTime) => {
        const newRemainingTime = prevRemainingTime - 1e3;
        if (newRemainingTime <= 1e3) {
          clearInterval(interval);
          if (onCountdownFinish) {
            onCountdownFinish();
          }
          return 0;
        }
        if (newRemainingTime <= 5 * 60 * 1e3 && on5MinutesLeft && count === 0) {
          on5MinutesLeft();
          setCount(1);
        }
        return newRemainingTime;
      });
    }, 1e3);
    return () => clearInterval(interval);
  }, [
    count,
    on5MinutesLeft,
    onCountdownFinish,
    calculateRemainingMilliseconds,
    deadline
  ]);
  useEffect(() => {
    if (isTimeout) {
      setRemainingTime(0);
    }
  }, [isTimeout]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-testid": "timeout-container",
      role: "timer",
      className: cn("flex items-center gap-0.5", containerClassName),
      children: [
        title && /* @__PURE__ */ jsx("div", { className: "md:text-sm", children: title }),
        isLoading ? /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-10" }) : remainingTime > 0 && /* @__PURE__ */ jsx(
          "div",
          {
            dir: "ltr",
            className: cn("flex min-w-14", descriptionClassName),
            "data-testid": "timeout",
            children: timeUnits.map(({ value, label, labelAr }, index) => {
              const timeSlotLabel = locale === "ar" ? labelAr : label;
              return /* @__PURE__ */ jsxs("div", { className: "flex center", children: [
                /* @__PURE__ */ jsxs(
                  "span",
                  {
                    className: cn(
                      "flex center gap-0.5",
                      !shouldLocalizeNumbers && locale === "ar" ? "flex-row-reverse" : "flex-row"
                    ),
                    "data-testid": `timeout-${timeSlotLabel}`,
                    children: [
                      formatNumberWithZeroPrefix(
                        value,
                        shouldLocalizeNumbers ? locale : "en"
                      ),
                      hasLabels && /* @__PURE__ */ jsx("span", { children: timeSlotLabel })
                    ]
                  },
                  index
                ),
                /* @__PURE__ */ jsx("span", { className: "mx-0.5", children: index < timeUnits.length - 1 && (customSeparator || separatorMap[separator]) })
              ] }, index);
            })
          }
        )
      ]
    }
  );
};
const Timer = React__default.memo(UnMemoizedTimer);
export {
  Timer
};
